/**
 * Organisation Realtime Collaboration Hook
 * 
 * Provides organisation data loading, realtime updates via Appwrite subscriptions,
 * and autosave functionality. Mirrors useCustomerCollaboration pattern.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import appwrite from '../services/appwrite';
import { getCustomerOrganisation, updateCustomerOrganisation } from '../services/api';
import { usePresence } from './usePresence';

const { getCurrentUser, subscribeToDocument } = appwrite;

function parseJsonField(val) {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try { return JSON.parse(val); } catch { return []; }
}

/**
 * Hook for collaboration on a specific organisation record
 * 
 * @param {string} organisationId - The organisation ID to collaborate on
 * @returns {object} Collaboration state and methods
 */
export function useOrganisationCollaboration(organisationId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const saveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef({});
  const {
    activeUsers,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  } = usePresence(organisationId ? `organisation:${organisationId}` : null);

  // Initialise current user
  useEffect(() => {
    const initUser = async () => {
      const user = await getCurrentUser();
      setCurrentUser(user);
    };
    initUser();
  }, []);

  // Load organisation data from API
  useEffect(() => {
    if (!organisationId) {
      setLoading(false);
      return;
    }

    const loadOrg = async () => {
      try {
        setLoading(true);
        const orgData = await getCustomerOrganisation(organisationId);
        // Parse JSON fields
        setData({
          ...orgData,
          phones: parseJsonField(orgData.phones),
          emails: parseJsonField(orgData.emails)
        });
      } catch (err) {
        console.error('Error loading organisation:', err);
      } finally {
        setLoading(false);
      }
    };

    loadOrg();
  }, [organisationId]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!organisationId) return;

    const unsubscribe = subscribeToDocument('customer_organisations', organisationId, (response) => {
      if (response.events?.includes('databases.*.collections.*.documents.*.update')) {
        setData(prev => {
          const payload = response.payload;
          return {
            ...prev,
            ...payload,
            phones: parseJsonField(payload.phones),
            emails: parseJsonField(payload.emails)
          };
        });
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [organisationId]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Autosave function
  const saveChanges = useCallback(async () => {
    if (!organisationId || Object.keys(pendingChangesRef.current).length === 0) return;

    const changesToSave = { ...pendingChangesRef.current };
    pendingChangesRef.current = {};

    try {
      setIsSyncing(true);
      await updateCustomerOrganisation(organisationId, changesToSave);
    } catch (err) {
      console.error('Error saving organisation changes:', err);
      // Re-add failed changes to pending
      pendingChangesRef.current = { ...pendingChangesRef.current, ...changesToSave };
    } finally {
      setIsSyncing(false);
    }
  }, [organisationId]);

  // Cleanup save timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveChanges();
      }
    };
  }, [saveChanges]);

  // Update field with debounced autosave
  const updateField = useCallback(async (fieldName, value, debounceMs = 1000) => {
    // Update local state immediately
    setData(prev => ({ ...prev, [fieldName]: value }));

    // Track pending change
    pendingChangesRef.current[fieldName] = value;

    // Debounce save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(saveChanges, debounceMs);
  }, [saveChanges]);

  return {
    data,
    loading,
    activeUsers,
    currentUser,
    isOnline,
    isSyncing,
    updateField,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  };
}
